import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import TextInput from '@narvar/nth-text-input';
import SelectList from '@narvar/nth-select-list';
import styled from 'styled-components';
import localizedCountries from '../../lib/localizedCountries';
import { COUNTRY_STATES } from '../../constants/geolocalization';
import { DIGITS_ONLY } from '../../constants/validationPatterns';

export const mapStateToProps = ({
  language,
  page: { countryCode, languageCode },
  returnsSettings: { returns: { selectedCountries } = {} } = {},
}) => ({
  language,
  countryCode,
  languageCode,
  selectedCountries,
});

const Container = styled.div`
  text-align: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  > h4 {
    margin: 1.5rem auto;
    &[data-test='address-validation-error'] {
      margin-bottom: 0;
    }
  }
  .sidebar-input {
    margin: 0.5rem 0;
    min-width: 0;
  }
  .sidebar-select {
    padding: 0 0.5rem;
  }
  > button {
    margin: 1.5rem 2rem;
  }
`;

const AddressForm = ({
  language,
  countryCode,
  languageCode,
  selectedCountries,
  address,
  onAddressChange,
  layout,
  incompleteFields,
}) => {
  const onChange = (elementProps, event) => {
    const { value } = event.target;
    const { type, key } = elementProps;
    if (type === 'number' && !DIGITS_ONLY.test(value)) {
      return;
    }
    onAddressChange({ key, value });
  };

  const selectInputOptions = (key) => {
    if (key === 'country') {
      const countries = localizedCountries(languageCode);
      return selectedCountries && selectedCountries.length
        ? countries.filter((country) => selectedCountries.includes(country.value))
        : countries;
    }
    if (key === 'province' || key === 'province_code') {
      const countryStates = COUNTRY_STATES[countryCode];
      if (!countryStates) return undefined;
      return countryStates.map((state) => ({
        value: state.state_name,
        label: state.state_name,
      }));
    }
    return undefined;
  };

  const getElement = (elementProps) => {
    const inputOptions = elementProps.allowedValues
      ? elementProps.allowedValues.map((value) => ({
          value,
          label: value,
        }))
      : selectInputOptions(elementProps.key);
    const displayTitle = `${
      elementProps.title || language[elementProps.languageKey] || elementProps.placeholder
    }
      ${elementProps.required ? '*' : ''}`;
    if (elementProps.type === 'select' && inputOptions) {
      return (
        <SelectList
          className="sidebar-select sidebar-input"
          id={elementProps.key}
          key={`address-form-${elementProps.key}`}
          defaultValue={displayTitle}
          value={address[elementProps.key]}
          onChange={(e) => onChange(elementProps, e)}
          s={{
            width: `${elementProps.width}%`,
            height: '3.125rem',
            color: '#000',
            ...(incompleteFields && incompleteFields.includes(elementProps.key)
              ? { borderColor: 'red' }
              : {}),
          }}
        >
          <option value={language[elementProps.languageKey] || elementProps.placeholder}>
            {displayTitle}
          </option>
          {inputOptions.map((option, index) => (
            <option
              key={`address-form-option-${option.value}`}
              value={option.value}
              data-test={`sidebar-select-option-${index}`}
            >
              {option.label}
            </option>
          ))}
        </SelectList>
      );
    }
    return (
      <TextInput
        className={`fs-exclude sidebar-input ${address[elementProps.key] ? 'filled-input' : ''}`}
        id={elementProps.key}
        key={`address-form-${elementProps.key}`}
        s={{
          width: `${elementProps.width}%`,
          ...(incompleteFields && incompleteFields.includes(elementProps.key)
            ? { borderColor: 'red' }
            : {}),
        }}
        type="text"
        onChange={(e) => {
          onChange(elementProps, e);
        }}
        defaultValue={address[elementProps.key]}
        value={address[elementProps.key]}
        label={displayTitle}
        labelStyle={{ color: '#a8a8a8' }}
        inputStyle={{ color: 'black' }}
      />
    );
  };

  return <Container>{layout.map((elementProps) => getElement(elementProps))}</Container>;
};

AddressForm.defaultProps = {
  languageCode: 'en',
  countryCode: 'US',
  address: {},
  selectedCountries: [],
  layout: undefined,
  incompleteFields: [],
};

AddressForm.propTypes = {
  onAddressChange: PropTypes.func.isRequired,
  address: PropTypes.object,
  language: PropTypes.object.isRequired,
  countryCode: PropTypes.string,
  languageCode: PropTypes.string,
  selectedCountries: PropTypes.array,
  layout: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      placeholder: PropTypes.string.isRequired,
      required: PropTypes.bool,
      minLength: PropTypes.number,
      maxLength: PropTypes.number,
      width: PropTypes.number,
    }),
  ),
  incompleteFields: PropTypes.arrayOf(PropTypes.string),
};

export default connect(mapStateToProps)(AddressForm);
